import './CopyrightInput.scss';
import React, { FC, useState, useMemo } from 'react';
import {
    CopyrightType,
    useGetCopyrightsQuery,
    useCreateCopyrightMutation,
    GetCopyrightsDocument,
} from '@/generated/graphql';
import { DEFAULT_COPYRIGHT_NID } from '@/store';
import Button from '@/components/basic/Button/Button';
import Switch from '@/components/basic/Switch/Switch'; // Ensure correct path
import Select from '@/components/basic/Select/Select'; // Ensure correct path
import Input from '@/components/basic/Input/Input';

// Shape of data this component strictly manages
export interface CopyrightData {
    copyright?: number | null;
    copyrightUrl?: string | null;
    newCopyrightName?: string | null;
    newCopyrightType?: CopyrightType | null;
    newCopyrightUrl?: string | null;
}

interface CopyrightInputProps {
    value: CopyrightData;
    onChange: (updatedFields: Partial<CopyrightData>) => void;
}

const COPYRIGHT_TYPE_OPTIONS = [
    { value: 'COPYRIGHT', label: 'Copyright' },
    { value: 'COPYLEFT', label: 'Copyleft' },
];

export const CopyrightInput: FC<CopyrightInputProps> = ({
    value,
    onChange,
}) => {
    const [isNewCopyright, setIsNewCopyright] = useState(false);

    const { data, loading } = useGetCopyrightsQuery();

    const [createCopyright, { loading: mutationLoading }] =
        useCreateCopyrightMutation({
            refetchQueries: [{ query: GetCopyrightsDocument }],
        });

    const handleCreateCopyright = async () => {
        try {
            const { data: result } = await createCopyright({
                variables: {
                    name: value.newCopyrightName ?? '',
                    copyrightType:
                        value.newCopyrightType ??
                        ('COPYRIGHT' as CopyrightType),
                    copyrightUrl: value.newCopyrightUrl ?? '',
                },
            });

            const newId = result?.createCopyright?.copyright?.id;

            if (newId) {
                setIsNewCopyright(false);
                onChange({
                    copyright: newId,
                    newCopyrightName: '',
                    newCopyrightUrl: '',
                });
            }
        } catch (e) {
            console.error('Error creating copyright:', e);
        }
    };

    const handleResetNewCopyright = () => {
        onChange({
            newCopyrightName: '',
            newCopyrightType: 'COPYRIGHT' as CopyrightType,
            newCopyrightUrl: '',
        });
    };

    const copyrightOptions = useMemo(() => {
        if (!data?.copyrights?.items) return [];
        return data.copyrights.items.map((item) => ({
            value: item?.id,
            label: item?.name ?? 'Unknown',
            type: item?.copyrightType,
        }));
    }, [data]);

    const selectedCopyright = copyrightOptions.find(
        (opt) => opt.value === (value.copyright ?? DEFAULT_COPYRIGHT_NID),
    );

    return (
        <div className="CopyrightInput">
            <Switch
                checked={isNewCopyright}
                inline
                addBottomGap
                rightLabel="Neuen Urheber anlegen"
                onChange={() => setIsNewCopyright(!isNewCopyright)}
            >
                Urheber wählen
            </Switch>
            {!isNewCopyright ? (
                <>
                    <Select
                        label="Urheber"
                        options={copyrightOptions}
                        value={value.copyright ?? DEFAULT_COPYRIGHT_NID}
                        required
                        labelWidth
                        disabled={loading}
                        onChange={(e) =>
                            onChange({ copyright: parseInt(e.target.value) })
                        }
                    />
                    {selectedCopyright?.type === 'COPYLEFT' && (
                        <Input
                            label="Foto-Lizenz URL"
                            placeholder="https://..."
                            value={value.copyrightUrl ?? ''}
                            type="text"
                            required
                            labelWidth
                            onChange={(e) =>
                                onChange({ copyrightUrl: e.target.value })
                            }
                        />
                    )}
                </>
            ) : (
                <>
                    <Input
                        label="Name des Urhebers"
                        value={value.newCopyrightName ?? ''}
                        type="text"
                        required
                        labelWidth
                        onChange={(e) =>
                            onChange({ newCopyrightName: e.target.value })
                        }
                    />
                    <Select
                        label="Lizenz-Typ"
                        options={COPYRIGHT_TYPE_OPTIONS}
                        value={value.newCopyrightType ?? 'COPYRIGHT'}
                        required
                        labelWidth
                        onChange={(e) =>
                            onChange({
                                newCopyrightType: e.target
                                    .value as CopyrightType,
                            })
                        }
                    />
                    {value.newCopyrightType === 'COPYLEFT' && (
                        <Input
                            label="Urheber-Webseite"
                            placeholder="https://..."
                            value={value.newCopyrightUrl ?? ''}
                            type="text"
                            required
                            labelWidth
                            onChange={(e) =>
                                onChange({ newCopyrightUrl: e.target.value })
                            }
                        />
                    )}
                    <div className="CopyrightInput_buttons">
                        <Button
                            variant="tertiary"
                            onClick={handleResetNewCopyright}
                            disabled={mutationLoading}
                        >
                            Zurücksetzen
                        </Button>
                        <Button
                            onClick={handleCreateCopyright}
                            disabled={
                                mutationLoading || !value.newCopyrightName
                            }
                        >
                            Urheber Speichern
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

export default CopyrightInput;
